import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MobileImageViewerProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onImageChange?: (index: number) => void;
  vehicleTitle?: string;
}

export const MobileImageViewer = ({ images, initialIndex, isOpen, onClose, onImageChange, vehicleTitle }: MobileImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouch, setLastTouch] = useState<{ x: number; y: number } | null>(null);
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null);
  const [swipeEndX, setSwipeEndX] = useState<number | null>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  // Reset when opening or changing image
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [currentIndex, isOpen]);

  // Notify parent when image changes
  useEffect(() => {
    if (isOpen && onImageChange) {
      onImageChange(currentIndex);
    }
  }, [currentIndex, isOpen, onImageChange]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Calculate distance between two touches
  const getDistance = (touch1: Touch, touch2: Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Single touch - for dragging (when zoomed) or swipe (when not zoomed)
      const touch = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
      setLastTouch(touch);
      setSwipeStartX(e.touches[0].clientX);
      setSwipeEndX(null);
      
      if (scale > 1) {
        setIsDragging(true);
      }
    } else if (e.touches.length === 2) {
      // Two touches - start pinch zoom
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialDistance(distance);
      setIsDragging(false);
      setSwipeStartX(null);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevent page scroll

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setSwipeEndX(touch.clientX);

      if (isDragging && scale > 1 && lastTouch) {
        // Single touch drag (only when zoomed in)
        const deltaX = touch.clientX - lastTouch.x;
        const deltaY = touch.clientY - lastTouch.y;

        setPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));

        setLastTouch({
          x: touch.clientX,
          y: touch.clientY
        });
      }
    } else if (e.touches.length === 2 && initialDistance) {
      // Pinch zoom
      const distance = getDistance(e.touches[0], e.touches[1]);
      const scaleChange = distance / initialDistance;
      const newScale = Math.min(Math.max(1, scale * scaleChange), 4); // Limit between 1x and 4x
      
      setScale(newScale);
      setInitialDistance(distance);

      // Reset position when zooming out to 1x
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  const handleTouchEnd = () => {
    // Handle swipe navigation when not zoomed
    if (scale === 1 && swipeStartX !== null && swipeEndX !== null) {
      const distance = swipeStartX - swipeEndX;
      const minSwipeDistance = 50;

      if (distance > minSwipeDistance && currentIndex < images.length - 1) {
        // Swipe left - next image
        setCurrentIndex(prev => prev + 1);
      } else if (distance < -minSwipeDistance && currentIndex > 0) {
        // Swipe right - previous image
        setCurrentIndex(prev => prev - 1);
      }
    }

    // Reset states
    setIsDragging(false);
    setLastTouch(null);
    setInitialDistance(null);
    setSwipeStartX(null);
    setSwipeEndX(null);
  };

  const handleDoubleTap = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black z-[100] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 backdrop-blur-sm">
        <div className="flex-1">
          <p className="text-white/60 text-xs">
            {currentIndex + 1} / {images.length}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/10"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Image Container */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={imageRef}
          className="w-full h-full flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleTap}
          style={{
            touchAction: 'none',
            cursor: scale > 1 ? 'grab' : 'zoom-in'
          }}
        >
          <img
            src={images[currentIndex]}
            alt={`Imagem ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            }}
            draggable={false}
          />
        </div>

        {/* Navigation via swipe gestures only - no arrows */}
      </div>

      {/* Bottom Indicators */}
      <div className="p-4 bg-black/50 backdrop-blur-sm">
        <div className="flex justify-center gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-1.5 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'w-8 bg-white' 
                  : 'w-1.5 bg-white/40'
              }`}
            />
          ))}
        </div>
        {scale > 1 && (
          <p className="text-center text-white/60 text-xs mt-2">
            Zoom: {scale.toFixed(1)}x
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};

